import * as React from 'react';

import { useDataProvider, FilterList, FilterListItem } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { SxProps } from '@mui/system';
import clsx from 'clsx';
import Button from '@mui/material/Button';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';

import { useStore, useListFilterContext } from 'react-admin';

export interface CustomFilterListProps {
    source?: string;
    optionValue?: string;
    optionText?: string;
    url: string;
    label: string;
    icon: any;
    variant?: 'filled' | 'outlined';
    selectionMode?: 'single' | 'checkbox';
    sx?: SxProps;
    className?: string;
    extraItems?: Array<{ label: string; value: Record<string, any> }>;
}

interface ResItem {
    item_id: any;
    item_title: any;
}

const CustomFilterList = (props: CustomFilterListProps) => {
    const {
        source = '',
        variant,
        //label = translate('ra.action.search'),
        optionValue = 'item_id',
        optionText = 'item_title',
        url,
        label,
        icon,
        className,
        extraItems,
        selectionMode = 'single',
        ...rest
    } = props;

    let params = '';

    const { resource, filterValues, setFilters } = useListFilterContext();

    const [filterListSettings, setFilterListSettings] = useStore(
        `${resource}.filterList`,
        ''
    );

    const getFilterListMode: any = () => {
        return filterListSettings !== '' &&
            filterListSettings[source] &&
            filterListSettings[source].mode
            ? filterListSettings[source].mode
            : '';
    };

    const setFilterListMode: any = c => {
        let settings =
            filterListSettings !== ''
                ? JSON.parse(JSON.stringify(filterListSettings))
                : {};
        settings = { ...settings, [source]: { mode: c } };
        setFilterListSettings(settings);
    };
    //console.log({...rest});
    const dataProvider = useDataProvider();
    // const [customChoices, setCustomChoices] = useState<ResItem[]>();
    params = 'source=' + source;

    const { data, error, isPending, refetch } = useQuery({
        queryKey: [url, 'getAny', params],
        queryFn: () => dataProvider.getAny(url, params),
    });

    if (!data) return null;

    const customChoices = data.data;

    if (!customChoices) return null;

    const handleOnClick: any = () => {
        getFilterListMode() !== ''
            ? setFilterListMode('')
            : setFilterListMode('_close');
    };

    // Checkbox multi-select logic
    const currentValue: string = filterValues[source] || '';
    const selectedValues = currentValue
        ? currentValue.split(',').filter(Boolean)
        : [];

    const handleCheckboxToggle = (value: string) => {
        let newSelected: string[];
        if (selectedValues.includes(value)) {
            newSelected = selectedValues.filter(v => v !== value);
        } else {
            newSelected = [...selectedValues, value];
        }

        if (newSelected.length === 0) {
            const { [source]: _, ...rest } = filterValues;
            setFilters(rest, undefined, false);
        } else {
            setFilters(
                { ...filterValues, [source]: newSelected.join(',') },
                undefined,
                false
            );
        }
    };

    return (
        <div style={{ display: 'grid' }}>
            <Button
                onClick={handleOnClick}
                className={clsx(`close_button${getFilterListMode()}`)}
            >
                {getFilterListMode() === '' ? (
                    <ArrowDropDownIcon />
                ) : (
                    <ArrowRightIcon />
                )}
            </Button>
            <FilterList
                label={label}
                icon={icon}
                className={clsx(
                    `OopFilterList${getFilterListMode()}`,
                    `oop-filterlist-${source}${getFilterListMode()}`,
                    className
                )}
                //onClick={handleOnClick}
                {...rest}
            >
                {selectionMode === 'checkbox' ? (
                    getFilterListMode() === '' && (
                        <FormGroup sx={{ pl: 2 }}>
                            {customChoices.map(choice => (
                                <FormControlLabel
                                    key={choice[optionValue]}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={selectedValues.includes(
                                                String(choice[optionValue])
                                            )}
                                            onChange={() =>
                                                handleCheckboxToggle(
                                                    String(choice[optionValue])
                                                )
                                            }
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            {choice[optionText]}
                                        </Typography>
                                    }
                                    sx={{ height: 28 }}
                                />
                            ))}
                        </FormGroup>
                    )
                ) : (
                    <>
                        {extraItems &&
                            extraItems.map((item, index) => (
                                <FilterListItem
                                    label={item.label}
                                    key={`extra_${index}`}
                                    value={item.value}
                                />
                            ))}
                        {customChoices.map(choice => (
                            <FilterListItem
                                label={choice[optionText]}
                                key={choice[optionValue]}
                                value={JSON.parse(
                                    `{"${source}": "${choice[optionValue]}"}`
                                )}
                            />
                        ))}
                    </>
                )}
            </FilterList>
        </div>
    );
};

export default CustomFilterList;
